/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DrawerBody, DrawerFooter, HStack, Stack } from '@chakra-ui/react';
import useQueryParams from 'utils/useQueryParams';
import FormButtons from 'components/FormButtons';
import { useFormDrawer } from 'context/FormDrawerContext';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Switch from 'components/Form/Switch';
import { LOYALTY_LEVEL_OPTIONS } from 'pages/SubscriptionUsers/Listing/constants';

const LOYALY_POINTS_FILTERS = [
  'loyaltyPoints',
  'loyaltyPoints_gte',
  'loyaltyPoints_lte',
];

function FiltersDrawer() {
  const { closeFormDrawer } = useFormDrawer();
  const { search, setSearch } = useQueryParams();

  const defaultValues = useMemo(() => {
    const {
      loyaltyPoints,
      loyaltyPoints_gte,
      loyaltyPoints_lte,
      isOutOfLoyaltyProgram,
      joinedLoyaltyProgram,
      ...params
    } = Object.fromEntries(new URLSearchParams(search));

    const loyaltyPointsOperator =
      loyaltyPoints_gte !== undefined
        ? 'gte'
        : loyaltyPoints_lte !== undefined
        ? 'lte'
        : loyaltyPoints !== undefined
        ? 'eq'
        : undefined;

    const loyaltyPointsValue =
      loyaltyPoints_gte ?? loyaltyPoints_lte ?? loyaltyPoints ?? undefined;

    return {
      ...params,
      isOutOfLoyaltyProgram: isOutOfLoyaltyProgram === 'true',
      joinedLoyaltyProgram: joinedLoyaltyProgram === 'true',
      loyaltyPoints: {
        operator: loyaltyPointsOperator,
        value: loyaltyPointsValue,
      },
    };
  }, [search]);

  const methods = useForm({
    mode: 'onChange',
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = handleSubmit(async (data) => {
    const { loyaltyPoints, ...fields } = data;

    if (loyaltyPoints?.operator && loyaltyPoints?.value) {
      const { operator, value } = loyaltyPoints;
      const name = `loyaltyPoints${
        operator && operator !== 'eq' ? `_${operator}` : ''
      }`;
      search.set(name, value);

      LOYALY_POINTS_FILTERS.filter((key) => key !== name).forEach((key) =>
        search.delete(key),
      );
    } else {
      LOYALY_POINTS_FILTERS.forEach((key) => search.delete(key));
    }

    Object.entries(fields).forEach(([key, value]) => {
      if (value) {
        const newValue =
          value instanceof Date ? value.toISOString() : value.toString();

        search.set(key, newValue);
      } else {
        search.delete(key);
      }
    });

    search.set('page', '1');
    setSearch(search);
    closeFormDrawer();
  });

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <DrawerBody>
        <FormProvider {...methods}>
          <Stack
            w="100%"
            h="100%"
            as="form"
            id="filters-drawer"
            onSubmit={onSubmit}
            spacing={4}
          >
            <Input name="email" label="Email" placeholder="Wpisz adres email" />
            <HStack spacing={2} alignItems="flex-end">
              <Select
                name="loyaltyPoints.operator"
                label="Ilość punktów"
                placeholder="Operator"
                options={[
                  { label: '>=', value: 'gte' },
                  { label: '<=', value: 'lte' },
                  { label: '=', value: 'eq' },
                ]}
                isClearable
              />
              <Input
                type="number"
                name="loyaltyPoints.value"
                placeholder="Ilość punktów"
              />
            </HStack>
            <Select
              name="loyaltyLevel"
              label="Poziom lojalności"
              placeholder="Poziom lojalności"
              options={LOYALTY_LEVEL_OPTIONS}
              isClearable
            />
            <Switch
              name="isOutOfLoyaltyProgram"
              label="Wyłączony z programu lojalnościowego"
            />
            <Switch
              name="joinedLoyaltyProgram"
              label="Dołączył do programu lojalnościowego"
            />
          </Stack>
        </FormProvider>
      </DrawerBody>
      <DrawerFooter>
        <FormButtons
          onCancel={closeFormDrawer}
          isLoading={false}
          isDisabled={false}
          formId="filters-drawer"
        />
      </DrawerFooter>
    </>
  );
}

export default FiltersDrawer;
